import { Inject, Injectable, OnDestroy } from "@angular/core";
import { EMPTY, NEVER, Observable, race, Subject } from "rxjs";
import {
  filter,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  timeout
} from "rxjs/operators";
import {
  AdminUpdateRoomStreamReq,
  AdminUpdateRoomStreamRes,
  AdminUpdateRoomStreamsOrderReq,
  AdminUpdateRoomStreamsOrderRes,
  RoomApi,
  SocketConnectData,
  SocketLifecycleEvent,
  SocketStateVO
} from "youm-lib";
import { IConfig } from "./interfaces";
import { YOUM_API_CONFIG_TOKEN } from "./youm-api.token";

@Injectable()
export class YoumAPIFacade implements OnDestroy {
  private static REQUEST_TIMEOUT_MS = 5000;

  private static ACTIONS = {
    ROOM_JOIN: "joinRoomAction",
    ROOM_ACCOUNT_DELETE: "roomAccountDeleteAction"
  };

  private static EVENTS = {
    ROOM_ACCOUNT_DELETE: "roomAccountDeleteEvent"
  };

  private readonly _roomApi: RoomApi;
  private readonly _connected$: Observable<boolean>;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(YOUM_API_CONFIG_TOKEN) private readonly _config: IConfig
  ) {
    this._roomApi = new RoomApi({
      url: this._config.url
    });
    this._connected$ = this._roomApi.connected$.pipe(shareReplay(1));
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get connected$() {
    return this._connected$;
  }

  get disconnect$() {
    return this._roomApi.disconnect$;
  }

  get connectError$() {
    return this._roomApi.connectError$;
  }

  get responseError$() {
    return this._roomApi.responseError$;
  }

  /** Room socket state: connected, reconnecting, failed... */
  get state$(): Observable<SocketStateVO> {
    return this._roomApi.state$;
  }

  /** Room socket disconnects, retries and results, for the logs */
  get lifecycle$(): Observable<SocketLifecycleEvent> {
    return this._roomApi.lifecycle$;
  }

  connect(config: SocketConnectData) {
    this._roomApi.connect(config);
  }

  /**
   * Never reconnected. With a cause, the room socket state becomes 'closed'
   * with that cause (the user is told the room connection is gone).
   */
  disconnect(cause?: string) {
    this._roomApi.disconnect(cause);
  }

  listRoomStreams<R, T>(data: T): Observable<R> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.listRoomStreams<R, T>(data))
    );
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStream(
    data: AdminUpdateRoomStreamReq
  ): Observable<AdminUpdateRoomStreamRes> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.adminUpdateRoomStream(data))
    );
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStreamsOrder(
    data: AdminUpdateRoomStreamsOrderReq
  ): Observable<AdminUpdateRoomStreamsOrderRes> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.adminUpdateRoomStreamsOrder(data))
    );
  }

  roomStreamEnd<T>(data: T): Observable<void> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.roomStreamEnd(data))
    );
  }

  restartStream<T>(data: T): Observable<void> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.restartStream(data))
    );
  }

  updateAudioStream<T>(data: T): Observable<void> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.updateAudioStream(data))
    );
  }

  roomStream<R, T>(data: T): Observable<R> {
    return race(
      this._connected$.pipe(filter((_) => _)),
      NEVER.pipe(timeout(YoumAPIFacade.REQUEST_TIMEOUT_MS))
    ).pipe(
      take(1),
      mergeMap(() => this._roomApi.roomStream<R, T>(data))
    );
  }

  listenErrorEvent() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenErrorEvent()),
      takeUntil(this._destroy$)
    );
  }

  listenRestartStreamEvent<T>() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenRestartStreamEvent<T>()),
      takeUntil(this._destroy$)
    );
  }

  listenUpdateAudioStreamEvent<T>() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenUpdateAudioStreamEvent<T>()),
      takeUntil(this._destroy$)
    );
  }

  listenAddDestinationEvent() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenAddDestination()),
      takeUntil(this._destroy$)
    );
  }

  listenUpdateDestinationEvent() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenUpdateDestination()),
      takeUntil(this._destroy$)
    );
  }

  listenDeleteDestinationEvent() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenDeleteDestination()),
      takeUntil(this._destroy$)
    );
  }

  listenErrorDestinationEvent() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenErrorDestination()),
      takeUntil(this._destroy$)
    );
  }

  listenLicenseLimitExceeded() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenLicenseLimitExceeded()),
      takeUntil(this._destroy$)
    );
  }

  listenRoomAccountDelete<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _
          ? this._roomApi.socket.listen<T>(
              YoumAPIFacade.EVENTS.ROOM_ACCOUNT_DELETE
            )
          : EMPTY
      )
    );
  }

  listenRoomBrandBgSet<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomBrandBgSet<T>() : EMPTY))
    );
  }

  listenRoomBrandBgDelete<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomBrandBgDelete<T>() : EMPTY))
    );
  }

  listenRoomBrandBgDeferredError() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandBgDeferredError() : EMPTY
      )
    );
  }

  listenRoomBrandCaptionLowerThirdSet<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandCaptionLowerThirdSet<T>() : EMPTY
      )
    );
  }

  listenRoomBrandCaptionLowerThirdDelete<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandCaptionLowerThirdDelete<T>() : EMPTY
      )
    );
  }

  listenRoomBrandCaptionLowerThirdDeferredError() {
    return this._connected$.pipe(
      switchMap((_) =>
        _
          ? this._roomApi.listenRoomBrandCaptionLowerThirdDeferredError()
          : EMPTY
      )
    );
  }

  listenRoomBrandLogoSet<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomBrandLogoSet<T>() : EMPTY))
    );
  }

  listenRoomBrandLogoDelete<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandLogoDelete<T>() : EMPTY
      )
    );
  }

  listenRoomBrandLogoDeferredError() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandLogoDeferredError() : EMPTY
      )
    );
  }

  listenRoomBrandOverlaySet<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandOverlaySet<T>() : EMPTY
      )
    );
  }

  listenRoomBrandOverlayDelete<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandOverlayDelete<T>() : EMPTY
      )
    );
  }

  listenRoomBrandOverlayDeferredError() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomBrandOverlayDeferredError() : EMPTY
      )
    );
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerError<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomMixerError<T>() : EMPTY))
    );
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerStart<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomMixerStart<T>() : EMPTY))
    );
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerStarted<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomMixerStarted<T>() : EMPTY))
    );
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerToRotate<T>() {
    return this._connected$.pipe(
      switchMap((_) => (_ ? this._roomApi.listenRoomMixerToRotate<T>() : EMPTY))
    );
  }

  listenRoomLiveStreamStatusUpdate<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomLiveStreamStatusUpdate<T>() : EMPTY
      )
    );
  }

  listenRoomStageLayoutDeferredError() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomStageLayoutDeferredError() : EMPTY
      )
    );
  }

  listenRoomStageLayoutStreamsSet<T>() {
    return this._connected$.pipe(
      switchMap((_) =>
        _ ? this._roomApi.listenRoomStageLayoutStreamsSet<T>() : EMPTY
      )
    );
  }

  listenRoomStreamEvent<T>() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenRoomStreamEvent<T>()),
      takeUntil(this._destroy$)
    );
  }

  listenRoomStreamEndEvent<T>() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenRoomStreamEndEvent<T>()),
      takeUntil(this._destroy$)
    );
  }

  listenRoomStreamUpdateEvent<T>() {
    return this._connected$.pipe(
      filter((_) => _),
      switchMap(() => this._roomApi.listenRoomStreamUpdateEvent<T>()),
      takeUntil(this._destroy$)
    );
  }

  roomAccountDelete<R, T>(data: T): Observable<R> {
    return this._roomApi.socket.emit<R, T>(
      YoumAPIFacade.ACTIONS.ROOM_ACCOUNT_DELETE,
      data
    );
  }

  roomJoin<T>(data: T): Observable<void> {
    return this._roomApi.socket.emit<void, T>(
      YoumAPIFacade.ACTIONS.ROOM_JOIN,
      data
    );
  }
}
